import React, { useState, useEffect } from 'react';
import { I } from '../../common/v5/config';
import '../../../css/v5/components/DailyInstructionField.css';

/**
 * DailyInstructionField - A modern, inline-editable field for temporary agent notes
 * 
 * @param {string} value - Current instruction text
 * @param {function} onSave - Callback when saving (receives new text)
 * @param {function} onClear - Callback when clearing the instruction
 * @param {boolean} editable - Whether the field can be edited
 */
const DailyInstructionField = ({
	value = "",
	onSave,
	onClear,
	editable = true
}) => {
	console.log("📝 DailyInstructionField received value prop:", value);
	const [isEditing, setIsEditing] = useState(false);
	const [text, setText] = useState(value);
	const [isHovered, setIsHovered] = useState(false);

	// Update local state when prop changes
	useEffect(() => {
		console.log("📝 DailyInstructionField useEffect - updating text to:", value);
		setText(value);
	}, [value]);

	const handleSave = () => {
		if (onSave) {
			onSave(text);
		}
		setIsEditing(false);
	};

	const handleCancel = () => {
		setText(value); // Reset to original value
		setIsEditing(false);
	};

	const handleClear = () => {
		if (confirm(I('Are you sure you want to clear this instruction?'))) {
			setText("");
			if (onClear) {
				onClear();
			}
		}
	};

	const handleEdit = () => {
		if (editable) {
			setIsEditing(true);
		}
	};

	// Empty state - show add button
	if (!value && !isEditing) {
		return editable ? (
			<div className="daily-instruction-field empty-state">
				<button 
					type="button"
					className="btn-add-instruction"
					onClick={handleEdit}
					data-qa-id="btn-add-daily-instruction"
				>
					<i className="icon-add"></i>
					<span>{I("Add daily instruction")}</span>
				</button>
			</div>
		) : null;
	}

	// Edit mode
	if (isEditing) {
		return (
			<div className="daily-instruction-field edit-mode">
				<div className="instruction-header">
					<i className="icon-pin"></i>
					<span className="title">{I("Daily Instruction")}</span>
				</div>
				<div className="instruction-body">
					<textarea
						id="dailyInstruction"
						name="dailyInstruction"
						className="instruction-textarea"
						value={text}
						onChange={(e) => setText(e.target.value)}
						rows={3}
						placeholder={I("Enter daily status, temporary note, or follow-up instruction here")}
						autoFocus={true}
					/>
				</div>
				<div className="instruction-actions">
					<button 
						type="button" 
						onClick={handleCancel} 
						className="btn-grey" 
						title={I("Cancel")} 
						data-qa-id="btn-cancel-instruction"
					>
						{I("Cancel")}
					</button>
					<button 
						type="button" 
						onClick={handleSave} 
						className="btn-blue" 
						title={I("Save")} 
						data-qa-id="btn-save-instruction"
					>
						{I("Save")}
					</button>
				</div>
			</div>
		);
	}

	// Display mode
	return (
		<div 
			className="daily-instruction-field display-mode"
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			<div className="instruction-header">
				<i className="icon-pin"></i>
				<span className="title">{I("Daily Instruction")}</span>
				{editable && isHovered && (
					<div className="header-actions">
						<i 
							className="icon-edit action-icon" 
							onClick={handleEdit}
							title={I("Edit")}
							data-qa-id="btn-edit-instruction"
						></i>
						<i 
							className="icon-trash action-icon delete" 
							onClick={handleClear}
							title={I("Clear")}
							data-qa-id="btn-clear-instruction"
						></i>
					</div>
				)}
			</div>
			<div className="instruction-content">
				{text.split('\n').map((line, index) => (
					<React.Fragment key={index}>
						{line}
						{index < text.split('\n').length - 1 && <br />}
					</React.Fragment>
				))}
			</div>
		</div>
	);
};

export default DailyInstructionField;

